import { Injectable } from '@angular/core';
import { TranslationService } from '../../../../../core/services/translation/translation.service';
import { ContextMenuItem } from '../classes/context-menu.item';
import { MenuAction } from 'src/app/shared/enums/menu.action';
import { GridContextPermissions } from '../classes/grid-context-permissions';
import { NetworkService } from 'src/app/core/services/network/network.service';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { KeepOfflineService } from 'src/app/core/services/keep-offline/keep-offline.service';
import { CollectionListDataInstance } from 'src/app/shared/interfaces/collection-list-data-instance';

@Injectable({
  providedIn: 'root',
})
export class ContextMenuItemService {

  public constructor(private translationService: TranslationService, private networkService: NetworkService, private pwaService: PwaService, private keepOfflineService: KeepOfflineService) {
  }


  /**
   * Returns the list of menuItems based on the permissions of the dataItem.
   * @private
   */
  public getMenuItems(permissions: GridContextPermissions, dataItem: CollectionListDataInstance, isMobileView: boolean, useSeparators: boolean): Partial<ContextMenuItem>[] {
    const menuItems: Partial<ContextMenuItem>[] = [];

    //in mobile view we need to add the cell actions too
    if (isMobileView) {
      if (permissions.showForwardTask) {
        menuItems.push({
          iconName: 'share',
          text: this.translationService.translate('ForwardTask'),
          action: MenuAction.ForwardTask,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }
      if (permissions.showRead || permissions.showReadTrashCan) {
        menuItems.push({
          iconName: 'eye',
          text: this.translationService.translate('Read'),
          action: MenuAction.Read,
        });
      }
      if (permissions.showEdit) {
        menuItems.push({
          iconName: 'pencil',
          text: this.translationService.translate('Edit'),
          action: MenuAction.Edit,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showExecuteSteps) {
        menuItems.push({
          iconName: 'bolt',
          text: this.translationService.translate('ExecuteSteps'),
          action: MenuAction.Execute,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showRecover) {
        menuItems.push({
          iconName: 'recycle',
          text: this.translationService.translate('RecoverFromTrashCan'),
          action: MenuAction.RecoverInstance,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showSubscribeToTraining) {
        menuItems.push({
          iconName: 'briefcase',
          text: this.translationService.translate('SubscribeToTraining'),
          action: MenuAction.SubscribeToTraining,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showSubscribeToExam) {
        menuItems.push({
          iconName: 'graduation-cap',
          text: this.translationService.translate('SubscribeToExam'),
          action: MenuAction.SubscribeToExam,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.canConsultRecord) {
      menuItems.push({
        iconName: 'arrow-up-right-from-square',
        text: this.translationService.translate('ConsultRecord'),
        action: MenuAction.ConsultRecord,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });
    }

    if (permissions.showDuplicateExams) {
      menuItems.push({
        iconName: 'copy',
        text: this.translationService.translate('DuplicateExam'),
        action: MenuAction.DuplicateExam,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });
    }

    if (permissions.showDownload || permissions.showViewOnline || permissions.showEditOnline) {
      if (permissions.showDownload) {
        menuItems.push({
          iconName: 'download',
          text: this.translationService.translate('DownloadFile'),
          action: MenuAction.DownloadFile,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }
      if (permissions.showViewOnline) {
        menuItems.push({
          iconName: 'eye',
          text: this.translationService.translate('ViewOnline'),
          action: MenuAction.ViewOnline,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }
      if (permissions.showEditOnline) {
        menuItems.push({
          iconName: 'pencil',
          text: this.translationService.translate('EditOnline'),
          action: MenuAction.EditOnline,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.viewFileHistory) {
        menuItems.push({
          iconName: 'clock',
          text: this.translationService.translate('FileHistory'),
          action: MenuAction.FileHistory,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.copyLinkToRecord || permissions.showDirectLink) {
      if (permissions.copyLinkToRecord) {
        menuItems.push({
          iconName: 'link',
          text: this.translationService.translate('LinkToRecord'),
          items: [{
            iconName: 'link',
            text: this.translationService.translate('CopyLink'),
            action: MenuAction.CopyLinkToRecord,
          },
            {
              iconName: 'qrcode',
              text: this.translationService.translate('DownloadQR'),
              action: MenuAction.DownloadQrCodeToRecord,
            }],
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showDirectLink) {
        menuItems.push({
          iconName: 'files',
          text: this.translationService.translate('DirectLink'),
          items: [{
            iconName: 'files',
            text: this.translationService.translate('CopyLink'),
            action: MenuAction.CopyDirectLink,
          },
            {
              iconName: 'qrcode',
              text: this.translationService.translate('DownloadQR'),
              action: MenuAction.DownloadQRCodeDirectLink,
            }],
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }   

    if (permissions.canKeepAvailableOffline && this.pwaService.isPwa()) {
      //check if the item is already available offline
      if (this.keepOfflineService.offlineInstanceCached(dataItem.CollectionsID!, dataItem.ID, dataItem.VersionsID, !!dataItem.FoldersID)) {
        menuItems.push({
          iconName: 'cloud-xmark',
          text: this.translationService.translate('Remove Offline Access'),
          action: MenuAction.RemoveOfflineAvailable,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      } else if (this.keepOfflineService.offlineInstanceFailed(dataItem.CollectionsID!, dataItem.ID, dataItem.VersionsID, !!dataItem.FoldersID)) {
        menuItems.push({
          iconName: 'cloud-arrow-down',
          text: this.translationService.translate('Make Available Offline (Retry)'),
          action: MenuAction.MakeOfflineAvailable,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      } else {
        menuItems.push({
          iconName: 'cloud-arrow-down',
          text: this.translationService.translate('Make Available Offline'),
          action: MenuAction.MakeOfflineAvailable,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showMinor || permissions.showMajor) {
      if (permissions.showMinor) {
        menuItems.push({
          iconName: 'm', //TODO: RV replace icon with lowercase m
          text: this.translationService.translate('CreateMinorChanges'),
          action: MenuAction.CreateMinorChanges,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showMajor) {
        menuItems.push({
          iconName: 'm',
          text: this.translationService.translate('CreateMajorChanges'),
          action: MenuAction.CreateMajorChanges,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showCheckOut || permissions.showCheckIn) {
      if (permissions.showCheckOut) {
        menuItems.push({
          iconName: 'file-arrow-down',
          text: this.translationService.translate('CheckOutFile'),
          action: MenuAction.CheckoutFile,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showCheckIn) {
        menuItems.push({
          iconName: 'file-arrow-up',
          text: this.translationService.translate('CheckInFile'),
          action: MenuAction.CheckInFile,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showCheckIn) {
        menuItems.push({
          iconName: 'upload',
          text: this.translationService.translate('PutFileWithoutCheckIn'),
          action: MenuAction.PutFileWithoutCheckIn,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showCheckIn) {
        menuItems.push({
          iconName: 'file-circle-minus',
          text: this.translationService.translate('UndoCheckoutFile'),
          action: MenuAction.UndoCheckoutFile,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showProperties || permissions.showPermissions) {
      if (permissions.showProperties) {
        menuItems.push({
          iconName: 'gear',
          text: this.translationService.translate('DocumentSettings'),
          action: MenuAction.DocumentSettings,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showPermissions) {
        menuItems.push({
          iconName: 'lock',
          text: this.translationService.translate('Permissions'),
          action: MenuAction.Permissions,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showFlowStatus || permissions.showMonitor || permissions.showHistory || permissions.showTrackChanges || permissions.showArchive) {
      if (permissions.showFlowStatus) {
        menuItems.push({
          iconName: 'chart-line',
          text: this.translationService.translate('FlowStatus'),
          action: MenuAction.FlowStatus,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showMonitor) {
        menuItems.push({
          iconName: 'display',
          text: this.translationService.translate('Monitor'),
          action: MenuAction.Monitor,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showHistory) {
        menuItems.push({
          iconName: 'clock-rotate-left',
          text: this.translationService.translate('History'),
          action: MenuAction.History,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showTrackChanges) {
        menuItems.push({
          iconName: 'clock-rotate-left',
          text: this.translationService.translate('TrackChanges'),
          action: MenuAction.TrackChanges,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      if (permissions.showArchive) {
        menuItems.push({
          iconName: 'box-archive',
          text: this.translationService.translate('Archive'),
          action: MenuAction.Archive,
          disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
        });
      }

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showRestartWorkflow) {
      menuItems.push({
        iconName: 'arrow-turn-down-left',
        text: this.translationService.translate('RestartWorkflow'),
        action: MenuAction.RestartWorkflow,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showReport) {
      menuItems.push({
        iconName: 'file-pdf',
        text: this.translationService.translate('DownloadReport'),
        action: MenuAction.Report,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });

      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showFolderShortcut && isMobileView) {
      menuItems.push({
        iconName: 'share',
        text: this.translationService.translate('Info_FolderShortCut'),
        action: MenuAction.FolderShortcut,
        cssClass: 'text-lighter',
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });
      useSeparators && menuItems.push({ separator: true, disabled: !this.pwaService.isOnline });
    }

    if (permissions.showUnlink) {
      menuItems.push({
        iconName: 'link-slash',
        text: this.translationService.translate('UnlinkFromFolder'),
        cssClass: '*:!text-red-400',
        action: MenuAction.UnlinkFromFolder,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });
    }

    if (permissions.showDelete || permissions.showDeleteFromFolder) {
      menuItems.push({
        iconName: 'xmark-large',
        text: this.translationService.translate('Delete'),
        cssClass: '*:!text-red-400',
        action: MenuAction.Delete,
        disabled: this.pwaService.isPwa() && !this.pwaService.isOnline,
      });
    }

    if (menuItems.length > 0 && menuItems[menuItems.length - 1].separator) {
      menuItems.pop();
    }

    return menuItems;
  }
}
